import { Box, Link, Typography } from "@mui/material";
import React, { FC, useCallback, useState } from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import { REACT_APP_CUSTOMER_PORTAL_URL } from "../../../../constants/envConstants";
import { iLoginFormProps } from "../../../../types/formtypes";
import { iLoginPayload } from "../../../../types/userType";
import CoreButtons from "./../../../core-components/button/CoreButtons";
import CheckboxLabel from "./../../../core-components/checkbox-label/CheckboxLabel";
import LabelInputField from "./../../../core-components/label-input-field/LabelInputField";
import { EMAIL_REGEX } from "../../../../constants/authConstants";
import "./loginForm.css";

export const ERROR_MESSAGES = {
	EMAIL_REQUIRED: "Email is required.",
	EMAIL_VALID: "Wrong email.",
	PASSWORD_REQUIRED: "Password is required.",
	PASSWORD_INVALID: "Wrong password.",
	COMMON_ERROR: "Wrong email or password.",
};

const LoginForm: FC<iLoginFormProps> = ({
	loginFormTitle,
	loginFormPara,
	loginFromFields,
	checkBoxLabel,
	buttonText,
	fullWidth,
	coreBtnPill,
	variant,
	color,
	onSubmit,
	formFieldStartText,
	formFieldLinkText,
	formFieldEndText,
	forgotPasswordLink,
}) => {
	const [rememberMe, setRememberMe] = useState(false);
	const [values, setValues] = useState<iLoginPayload>({
		email: "",
		password: "",
	});
	const [showCommonError, setShowCommonError] = useState(false);

	const [touched, setTouched] = useState<{ [key: string]: boolean }>({
		email: false,
		password: false,
	});

	const [errors, setErrors] = useState<{ [key: string]: string }>({
		email: ERROR_MESSAGES.EMAIL_REQUIRED,
		password: ERROR_MESSAGES.PASSWORD_REQUIRED,
	});
	const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
		let error = "";
		const value = event.target.value;
		if (event.target.id === "email") {
			const isEmailValid = EMAIL_REGEX.test(value.toString());
			if (!event.target.value) {
				error = ERROR_MESSAGES.EMAIL_REQUIRED;
			} else if (!isEmailValid) {
				error = ERROR_MESSAGES.EMAIL_VALID;
			} else {
				error = "";
			}
		} else if (event.target.id === "password" && !value) {
			error = ERROR_MESSAGES.PASSWORD_REQUIRED;
		}
		setValues({ ...values, [event.target.id]: event.target.value });
		setTouched({ ...touched, [event.target.id]: false });
		setErrors({ ...errors, [event.target.id]: error });
		setShowCommonError(false);
	};

	const blurHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setTouched({ ...touched, [event.target.id]: true });
		},
		[setTouched, touched]
	);
	const keyEventhandler = (event: any) => {
		if (event.key === "Enter") {
			event.preventDefault();
			onSubmit({ ...values }, () => {
				setShowCommonError(true);
			});
		}
	};

	return (
		<>
			<Box component="form" className="login-form">
				<Box className="head">
					<Typography variant="h1">{loginFormTitle}</Typography>
					<Typography variant="body2">{loginFormPara}</Typography>
				</Box>
				<Box className="login-form-hold">
					{loginFromFields.map((item, index) => (
						<LabelInputField
							key={index}
							id={item.id}
							label={item.label}
							type={item.type}
							onChange={changeHandler}
							placeholder={item.placeholder}
							className={item.className}
							error={
								!!touched[item.id as string] &&
								!!errors[item.id as string]
							}
							helperText={touched[item.id] ? errors[item.id] : ""}
							required={item.required}
							disabled={item.disabled}
							{...item.loginFromFieldsProps}
							onBlur={blurHandler}
							keyUpHanlder={keyEventhandler}
						/>
					))}
					{showCommonError && (
						<span
							style={{
								display: "inline-block",
								color: "#B42318",
								marginBlockEnd: 10,
								marginBlockStart: "-4px",
								fontSize: 14,
								fontWeight: 500,
							}}
						>
							{ERROR_MESSAGES.COMMON_ERROR}
						</span>
					)}
					<Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
						<CheckboxLabel
							label={checkBoxLabel}
							className="form-group login-checkbox-label remember-me-checkbox"
							checked={rememberMe}
							onChange={() => setRememberMe(!rememberMe)}
						/>
						{forgotPasswordLink && (
							<Link
								component={ReactRouterLink}
								to={forgotPasswordLink.href}
								variant="body2"
								underline="hover"
							>
								{forgotPasswordLink.label}
							</Link>
						)}
					</Box>
					<CoreButtons
						buttonText={buttonText}
						fullWidth={fullWidth}
						disabled={
							!values.email ||
							!values.password ||
							(touched.email && !!errors.email) ||
							(touched.password && !!errors.password)
						}
						coreBtnPill={coreBtnPill}
						variant={variant}
						color={color}
						onclick={() => {
							onSubmit({ ...values }, () => {
								setShowCommonError(true);
							});
						}}
					/>
				</Box>
				<Typography
					className="login-form-bottom-text"
					variant="button"
					gutterBottom
					sx={{
						display: "block"
					}}
				>
					{formFieldStartText}{" "}
					<Link
						href={`${REACT_APP_CUSTOMER_PORTAL_URL}/en/partner`}
						variant="subtitle2"
						underline="none"
						target="_blank"
					>
						{formFieldLinkText}
					</Link>{" "}
					{formFieldEndText}
				</Typography>
			</Box>
		</>
	);
};

export default LoginForm;
